import { apiClient } from './api';
import {
  Species,
  CreateSpeciesDTO,
  UpdateSpeciesDTO,
  SpeciesFeatures,
  ExtractedFeaturesResponse,
  BulkComputeResult,
  CompanionEffect,
  CompanionSpectrum,
} from '../types/plant-o-pedia';

export const speciesService = {
  getAllSpecies: async (): Promise<Species[]> => {
    const response = await apiClient.get<{ success: boolean; data: Species[] }>('/species');
    return response.data;
  },

  getSpeciesById: async (id: string): Promise<Species> => {
    const response = await apiClient.get<{ success: boolean; data: Species }>(`/species/${id}`);
    return response.data;
  },

  createSpecies: async (data: CreateSpeciesDTO): Promise<Species> => {
    const response = await apiClient.post<{ success: boolean; data: Species }>('/species', data);
    return response.data;
  },

  updateSpecies: async (id: string, data: UpdateSpeciesDTO): Promise<Species> => {
    const response = await apiClient.put<{ success: boolean; data: Species }>(
      `/species/${id}`,
      data,
    );
    return response.data;
  },

  deleteSpecies: async (id: string): Promise<void> => {
    await apiClient.delete(`/species/${id}`);
  },

  /**
   * Get sowing recommendations for the specified month (0-11)
   */
  getSowingRecommendations: async (month?: number, temp?: number): Promise<Species[]> => {
    const params = new URLSearchParams();
    if (month !== undefined) params.set('month', String(month));
    if (temp !== undefined) params.set('temp', String(Math.round(temp)));
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<{ success: boolean; data: Species[] }>(
      `/species/sowing/recommendations${query}`,
    );
    return response.data;
  },

  /**
   * Vyvolá AI obohacení dat (nová verze s JSON mode a Zod)
   */
  aiEnrich: async (name: string): Promise<Partial<Species>> => {
    const response = await apiClient.post<{ success: boolean; data: Partial<Species> }>(
      '/species/ai-enrich', 
      { name },
      { timeout: 45000 }
    );
    return response.data;
  },

  // ============================================================================
  // SPECIES FEATURES (FAZE 2: ML-Ready)
  // ============================================================================

  /**
   * Ziska vsechny ML features pro species
   * @param reliableOnly - pouze species s dostatkem dat pro spolehlivou statistiku
   */
  getAllFeatures: async (reliableOnly = false): Promise<SpeciesFeatures[]> => {
    const response = await apiClient.get<{
      success: boolean;
      data: SpeciesFeatures[];
      count: number;
    }>(`/species-features${reliableOnly ? '?reliable=true' : ''}`);
    return response.data;
  },

  /**
   * Ziska ML features pro konkretni species
   */
  getFeaturesBySpeciesId: async (speciesId: string): Promise<SpeciesFeatures | null> => {
    try {
      const response = await apiClient.get<{ success: boolean; data: SpeciesFeatures }>(
        `/species-features/${speciesId}`,
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Extrahuje staticke features z Species JSONB (Admin only)
   */
  extractFeatures: async (speciesId: string): Promise<ExtractedFeaturesResponse> => {
    const response = await apiClient.post<{ success: boolean; data: ExtractedFeaturesResponse }>(
      `/species-features/${speciesId}/extract`,
    );
    return response.data;
  },

  /**
   * Prepocita computed stats z batches/harvests (Admin only)
   */
  computeStats: async (speciesId: string): Promise<any> => {
    const response = await apiClient.post<{ success: boolean; data: any }>(
      `/species-features/${speciesId}/compute-stats`,
    );
    return response.data;
  },

  /**
   * Kompletni refresh features pro species (Admin only)
   */
  refreshFeatures: async (
    speciesId: string,
  ): Promise<{
    extracted: ExtractedFeaturesResponse;
    computed: any;
    features: SpeciesFeatures | null;
  }> => {
    const response = await apiClient.post<{ success: boolean; data: any }>(
      `/species-features/${speciesId}/refresh`,
    );
    return response.data;
  },

  /**
   * Bulk compute features pro vsechny species (Admin only)
   */
  computeAllFeatures: async (): Promise<BulkComputeResult> => {
    const response = await apiClient.post<{ success: boolean; data: BulkComputeResult }>(
      `/species-features/compute-all`,
    );
    return response.data;
  },
  /**
   * Semantické vyhledávání pomocí AI (Phase 2B)
   */
  semanticSearchSpecies: async (query: string, limit = 10): Promise<any[]> => {
    const response = await apiClient.get<{ success: boolean; data: any[] }>(
      `/search/species?q=${encodeURIComponent(query)}&limit=${limit}`,
    );
    return response as any; // The backend returns the array directly in this case
  },

  /**
   * Spustí plnou synchronizaci AI embeddingů (Admin only)
   */
  syncEmbeddings: async (): Promise<{ species: number; pests: number; diseases: number }> => {
    const response = await apiClient.post<{ success: boolean; data: any }>(
      '/search/sync',
      {},
      { timeout: 60000 },
    );
    return response as any;
  },

  /**
   * AI asistent — vyplní parametry druhu podle názvu (gpt-4o-mini)
   */
  aiSuggest: async (name: string, scientific_name?: string): Promise<any> => {
    const response = await apiClient.post<{ success: boolean; data: any }>(
      '/species/ai-suggest',
      { name, scientific_name },
      { timeout: 60000 }, // OpenAI kompletní profil trvá 30-35s
    );
    return response.data;
  },

  /**
   * AI asistent — doplní POUZE sousedy (good/bad companions) pro daný druh
   */
  aiSuggestCompanions: async (name: string, scientific_name?: string): Promise<any> => {
    const response = await apiClient.post<{ success: boolean; data: any }>(
      '/species/ai-suggest-companions',
      { name, scientific_name },
      { timeout: 30000 },
    );
    return response.data;
  },

  /**
   * AI asistent — doplní POUZE škůdce a nemoci pro daný druh
   */
  aiSuggestPests: async (name: string, scientific_name?: string): Promise<any> => {
    const response = await apiClient.post<{ success: boolean; data: any }>(
      '/species/ai-suggest-pests',
      { name, scientific_name },
      { timeout: 45000 },
    );
    return response.data;
  },

  // ============================================================
  // PlantingSense — Companion Spectrum & Effects
  // ============================================================

  /** Fetch companion spectrum (human data + GNN predictions) for a species */
  getCompanionSpectrum: async (speciesId: string, context = 'spatial'): Promise<CompanionSpectrum> => {
    const response = await apiClient.get<CompanionSpectrum>(`/species/${speciesId}/companions/spectrum?context=${context}`);
    console.log('[SpeciesService] getCompanionSpectrum raw:', response);
    return response as unknown as CompanionSpectrum;
  },

  /** List companion effects (filtered by user role) */
  getCompanionEffects: async (): Promise<CompanionEffect[]> => {
    const response = await apiClient.get<CompanionEffect[]>('/companion-effects');
    console.log('[SpeciesService] getCompanionEffects raw:', response);
    return response;
  },

  /** Create a new companion effect */
  createCompanionEffect: async (data: Partial<CompanionEffect>): Promise<CompanionEffect> => {
    const response = await apiClient.post<CompanionEffect>('/companion-effects', data);
    return response;
  },

  /** Update a companion effect */
  updateCompanionEffect: async (id: string, data: Partial<CompanionEffect>): Promise<CompanionEffect> => {
    const response = await apiClient.put<CompanionEffect>(`/companion-effects/${id}`, data);
    return response;
  },

  /** Delete a companion effect */
  deleteCompanionEffect: async (id: string): Promise<void> => {
    await apiClient.delete(`/companion-effects/${id}`);
  },

  /** Publish a companion effect (admin only) */
  publishCompanionEffect: async (id: string): Promise<CompanionEffect> => {
    const response = await apiClient.patch<CompanionEffect>(`/companion-effects/${id}/publish`);
    return response;
  },

  /** Verify G-Root prediction */
  verifyGnnPrediction: async (speciesId: string, companionId: string, benefitScore: number, harmScore: number, context: 'companion' | 'rotation' = 'companion'): Promise<void> => {
    await apiClient.post(`/species/${speciesId}/companions/gnn/verify`, {
      companion_species_id: companionId,
      benefit_score: benefitScore,
      harm_score: harmScore,
      context,
    });
  },

  /** Znalostní graf - Přidání ověření / verifikace (Admin/God) */
  addKnowledgeGraphVerification: async (
    entityId: string,
    entityType: 'companion' | 'relationship',
    source: string,
  ): Promise<{ cumulative_weight: number; is_verified: boolean; message: string }> => {
    const response = await apiClient.post<any>('/species/knowledge-graph/verification', {
      entity_id: entityId,
      entity_type: entityType,
      source,
    });
    return response;
  },


  /** Reject G-Root prediction */
  rejectGnnPrediction: async (speciesId: string, companionId: string, context: 'companion' | 'rotation' = 'companion'): Promise<void> => {
    await apiClient.post(`/species/${speciesId}/companions/gnn/reject`, {
      companion_species_id: companionId,
      context,
    });
  },

  /** Delete a verified companion relationship */
  deleteCompanion: async (speciesId: string, companionSpeciesId: string): Promise<void> => {
    await apiClient.delete(`/species/${speciesId}/companions/${companionSpeciesId}`);
  },

  /** Update a verified companion relationship */
  updateCompanion: async (speciesId: string, companionSpeciesId: string, data: {
    benefit_score?: number;
    harm_score?: number;
    comment?: string;
    relation_type?: string;
    reasoning?: string;
    effect_ids?: string[];
  }): Promise<void> => {
    await apiClient.put(`/species/${speciesId}/companions/${companionSpeciesId}`, data);
  },

  /** Automaticke overeni companion paru proti znalostni bazi */
  ragVerifyCompanion: async (companionId: string): Promise<{ reasoning: string; source_ids: string[] }> => {
    const response = await apiClient.post<{ success: boolean; data: { reasoning: string; source_ids: string[] } }>(
      `/species/companions/${companionId}/rag-verify`,
      {},
      { timeout: 60000 }
    );
    return (response as any).data;
  },

  // ============================================================
  // Knowledge Sources — RAG pipeline admin
  // ============================================================

  /** Nacist seznam knowledge sources */
  getKnowledgeSources: async (): Promise<any[]> => {
    const response = await apiClient.get<{ success: boolean; data: any[] }>('/knowledge-sources');
    return (response as any).data;
  },

  /** Vytvorit novy knowledge source */
  createKnowledgeSource: async (data: {
    type: string; title: string; author?: string; publication_year?: number;
    publisher?: string; url?: string; doi?: string; isbn?: string;
    credibility_score?: number; description?: string; language?: string;
  }): Promise<any> => {
    const response = await apiClient.post<{ success: boolean; data: any }>('/knowledge-sources', data);
    return (response as any).data;
  },

  /** Manualni re-ingest zdroje */
  reingestKnowledgeSource: async (sourceId: string): Promise<any> => {
    const response = await apiClient.post<{ success: boolean; data: any }>(`/knowledge-sources/${sourceId}/ingest`);
    return (response as any).data;
  },

  /** Smazani zdroje */
  deleteKnowledgeSource: async (sourceId: string): Promise<any> => {
    const response = await apiClient.delete<{ success: boolean; data: any }>(`/knowledge-sources/${sourceId}`);
    return (response as any).data;
  },

  /** Ask backend to trigger the Python G-Root generation */
  generateGnnPredictions: async (): Promise<{ count: number; data: any[]; summary: string[] }> => {
    const response = await apiClient.post<{ count: number; data: any[]; summary: string[] }>('/species/companions/gnn-predict', {}, { timeout: 60000 });
    return response as any;
  },

  /** Get all current GNN predictions */
  getAllGnnPredictions: async (context: 'companion' | 'rotation' = 'companion'): Promise<{ success: boolean; data: any[] }> => {
    const response = await apiClient.get<{ success: boolean; data: any[] }>(`/species/companions/gnn-predictions?context=${context}`);
    return response;
  },

  /** Incremental background GNN discovery — finds uncovered pairs and predicts them */
  discoverNewEdges: async (context: 'companion' | 'rotation' = 'companion', batchSize = 10): Promise<{
    success: boolean;
    analyzed: number;
    discoveries: any[];
    remaining: number;
  }> => {
    const response = await apiClient.post<any>('/species/companions/gnn-discover', { context, batchSize }, { timeout: 120000 });
    return response;
  },

  /** Perform specific pairwise GNN logic (Lemon Balm + Fennel style) */
  checkGnnPair: async (idA: string, idB: string, context = 'spatial'): Promise<any> => {
    const response = await apiClient.post<{ success: boolean; data: any }>('/species/gnn-check-pair', {
      idA,
      idB,
      context,
    }, { timeout: 30000 });
    return response.data ?? response;
  },

  getTaxonomy: async (): Promise<TaxonomyData> => {
    const response = await apiClient.get<{ success: boolean; data: TaxonomyData }>('/species/taxonomy');
    return response.data;
  },

  getNutrientProfiles: async (speciesId: string): Promise<NutrientProfilesResponse> => {
    const response = await apiClient.get<NutrientProfilesResponse>(`/species/${speciesId}/nutrient-profiles`);
    return (response as any).data ?? response;
  },

  saveNutrientProfiles: async (speciesId: string, profiles: NutrientProfileEntry[]): Promise<void> => {
    await apiClient.put(`/species/${speciesId}/nutrient-profiles`, { profiles });
  },
};

export interface TaxonomyGenus {
  id: string;
  name: string | null;
  scientific_name: string;
  species_count: number;
  species: Array<{
    id: string;
    name: string;
    icon: string | null;
    category: string;
    difficulty: number;
  }>;
}

export interface TaxonomyFamily {
  family_id: string;
  family_name: string;
  family_lat: string;
  nutrient_impact: string | null;
  genera: TaxonomyGenus[];
  species_count: number;
  genus_count: number;
}

export interface TaxonomyData {
  families: TaxonomyFamily[];
  orphan_genera: TaxonomyGenus[];
}

export interface NutrientDefinition {
  code: string;
  name_cs: string;
  name_en: string;
  category: 'macro' | 'secondary' | 'micro';
  gnn_weight: number;
  display_order: number;
}

export interface NutrientProfileEntry {
  phase_name: string;
  nutrient_code: string;
  demand: number;
  production: number;
}

export interface NutrientProfilesResponse {
  profiles: NutrientProfileEntry[];
  definitions: NutrientDefinition[];
}
